import { useState } from "react";
import { useAuth } from "../components/AuthContext";
import { notifyPolicyUpdate } from "../api/admin";
import toast from "react-hot-toast";
import {
  Mail,
  FileText,
  Shield,
  Send,
  CheckCircle,
  XCircle,
  Loader,
  AlertCircle,
} from "lucide-react";

const PolicyUpdateNotification = () => {
  const { adminToken } = useAuth();
  const [selectedPolicy, setSelectedPolicy] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSendNotification = async () => {
    if (!selectedPolicy) {
      toast.error("Please select a policy type");
      return;
    }

    if (!adminToken) {
      toast.error("Admin authentication required");
      return;
    }

    const policyName = selectedPolicy === "privacy" ? "Privacy Policy" : "Terms and Conditions";

    // Confirm before sending
    const confirmed = window.confirm(
      `Are you sure you want to send ${policyName} update notifications to all users? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      setResult(null);

      const response = await notifyPolicyUpdate(selectedPolicy, adminToken);

      if (response.success) {
        setResult({
          success: true,
          policyName,
          totalUsers: response.totalUsers,
          notified: response.notified,
          failed: response.failed,
          failedEmails: response.failedEmails,
        });
        toast.success(
          `Successfully sent ${response.notified} notification(s)!`
        );
      } else {
        setResult({
          success: false,
          message: response.message || "Failed to send notifications",
        });
        toast.error(response.message || "Failed to send notifications");
      }
    } catch (error) {
      console.error("Error sending policy update notification:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to send notifications";
      setResult({
        success: false,
        message: errorMessage,
      });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
          <Mail className="w-6 h-6 text-red-600 dark:text-red-400" />
          Policy Update Notifications
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Send email notifications to all users when Privacy Policy or Terms and Conditions are updated.
        </p>
      </div>

      {/* Policy Selection */}
      <div className="backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 rounded-xl p-6 border border-white/20 dark:border-gray-600/30 shadow-lg">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Select Policy Type
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Privacy Policy Option */}
          <button
            onClick={() => setSelectedPolicy("privacy")}
            disabled={loading}
            className={`p-4 rounded-xl border-2 transition-all duration-300 ${selectedPolicy === "privacy"
                ? "border-red-500 bg-red-50 dark:bg-red-900/20 dark:border-red-400"
                : "border-gray-200 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-700 bg-white dark:bg-gray-800"
              } ${loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-lg ${selectedPolicy === "privacy"
                    ? "bg-red-500 text-white"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                  }`}
              >
                <Shield className="w-5 h-5" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-800 dark:text-gray-200">
                  Privacy Policy
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Notify users about privacy policy updates
                </div>
              </div>
            </div>
          </button>
          

          {/* Terms and Conditions Option */}
          <button
            onClick={() => setSelectedPolicy("terms")}
            disabled={loading}
            className={`p-4 rounded-xl border-2 transition-all duration-300 ${selectedPolicy === "terms"
                ? "border-red-500 bg-red-50 dark:bg-red-900/20 dark:border-red-400"
                : "border-gray-200 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-700 bg-white dark:bg-gray-800"
              } ${loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-lg ${selectedPolicy === "terms"
                    ? "bg-red-500 text-white"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                  }`}
              >
                <FileText className="w-5 h-5" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-800 dark:text-gray-200">
                  Terms & Conditions
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Notify users about terms and conditions updates
                </div>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Send Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSendNotification}
          disabled={!selectedPolicy || loading}
          className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 dark:from-red-600 dark:to-red-700 text-white rounded-xl font-semibold shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-2"
        >
          {loading ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Send Notifications
            </>
          )}
        </button>
      </div>

      {/* Result Display */}
      {result && (
        <div
          className={`backdrop-blur-sm rounded-xl p-6 border-2 ${result.success
              ? "bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400"
              : "bg-red-50 dark:bg-red-900/20 border-red-500 dark:border-red-400"
            } shadow-lg`}
        >
          <div className="flex items-start gap-3">
            {result.success ? (
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              {result.success ? (
                <>
                  <h3 className="font-semibold text-green-800 dark:text-green-300 mb-2">
                    Notifications Sent Successfully!
                  </h3>
                  <div className="space-y-1 text-sm text-green-700 dark:text-green-400">
                    <p>
                      <strong>Policy:</strong> {result.policyName}
                    </p>
                    <p>
                      <strong>Total Users:</strong> {result.totalUsers}
                    </p>
                    <p>
                      <strong>Successfully Notified:</strong> {result.notified}
                    </p>
                    {result.failed > 0 && (
                      <>
                        <p>
                          <strong>Failed:</strong> {result.failed}
                        </p>
                        {result.failedEmails && result.failedEmails.length > 0 && (
                          <div className="mt-2">
                            <p className="font-medium mb-1">Failed Emails:</p>
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-2 max-h-32 overflow-y-auto">
                              <ul className="list-disc list-inside text-xs space-y-1">
                                {result.failedEmails.slice(0, 10).map((email, idx) => (
                                  <li key={idx}>{email}</li>
                                ))}
                                {result.failedEmails.length > 10 && (
                                  <li className="text-gray-500">
                                    ... and {result.failedEmails.length - 10} more
                                  </li>
                                )}
                              </ul>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <h3 className="font-semibold text-red-800 dark:text-red-300 mb-2">
                    Failed to Send Notifications
                  </h3>
                  <p className="text-sm text-red-700 dark:text-red-400">
                    {result.message}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="backdrop-blur-sm bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-medium mb-1">Important Information:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Emails will be sent from: {typeof window !== 'undefined' && (window.location.hostname === 'unmaapp.com' || window.location.hostname.includes('unmaapp.com')) ? 'info.unmaapp@atozasindia.in' : 'infounma@testatozas.in'}</li>
              <li>Only users with verified email addresses will receive notifications</li>
              <li>Emails are sent in batches to avoid rate limiting</li>
              <li>This action cannot be undone</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyUpdateNotification;



